import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { _labelExtract } from 'scaleflow/helpers/label-extract';
import { STATUS_KEYS } from 'scaleflow/models/initiative-update';
import capitalize from 'scaleflow/utils/capitalize';

const sortUnspecifiedColumnToLast = (a, b) => {
  if (a === 'Unspecified') {
    return 1; // 'Unspecified' should be after anything else
  } else if (b === 'Unspecified') {
    return -1; // Anything else should be before 'Unspecified'
  } else {
    return a.localeCompare(b); // Sort alphabetically for other elements
  }
};

export default class RoadmapViewIndexComponent extends Component {
  @service initiativeSlideover;
  @service intl;
  @service betaToggle;

  rowLabelKeys = {
    teams: 'name',
    theme: 'title',
    status: 'self',
    source: 'self',
    progress: 'self',
    goals: 'title',
  };

  columnLabelKeys = {
    teams: 'name',
    theme: 'title',
    status: 'self',
    source: 'self',
    progress: 'self',
    goals: 'title',
  };

  filtersWithHasManyRelationships = ['teams', 'goals'];

  get fields() {
    return this.args.fields;
  }

  get initiatives() {
    return this.args.initiatives ?? [];
  }

  get columnFilter() {
    return this.args.columnFilter ?? 'source';
  }

  get rowFilter() {
    return this.args.rowFilter ?? 'status';
  }

  get columns() {
    const unspecified = this.intl.t('dictionary.unspecified');

    const _columns = this.initiatives.reduce((acc, initiative) => {
      const hasMany = this.filtersWithHasManyRelationships.includes(
        this.columnFilter,
      );
      const columnObj = initiative[this.columnFilter];
      const labelKey = this.columnLabelKeys[this.columnFilter];
      let title = '';

      if (
        !columnObj ||
        (columnObj.promise &&
          (hasMany ? !columnObj.content : !columnObj?.get('content')))
      ) {
        title = unspecified;
        if (title && !acc.some((item) => item.title === title))
          acc.push({ title });
      } else if (!hasMany) {
        title = labelKey === 'self' ? columnObj : columnObj.get(labelKey);
        if (title && !acc.some((item) => item.title === title))
          acc.push({ title });
      } else {
        if (!columnObj?.content?.length) {
          title = unspecified;
          if (title && !acc.some((item) => item.title === title))
            acc.push({ title });
        } else
          columnObj?.content?.forEach((item) => {
            const title = item.get(labelKey);
            if (title && !acc.some((item) => item.title === title))
              acc.push({ title });
          });
      }

      return acc;
    }, []);
    if (this.columnFilter === 'status')
      _columns.sort((a, b) =>
        this.sortStatusWithUnspecifiedToLast(a.title, b.title),
      );
    else if (this.columnFilter === 'source')
      _columns.sort((a, b) =>
        this.sortSourceWithUnspecifiedToLast(a.title, b.title),
      );
    else if (_columns.some((item) => item.title === unspecified))
      _columns.sort((a, b) => sortUnspecifiedColumnToLast(a.title, b.title));
    _columns.forEach((item) => {
      item.visible = !this.hiddenColumns[item.title];
    });

    if (
      !this.args.whitelist ||
      (typeof this.args.whitelist === 'object' &&
        Object.keys(this.args.whitelist).length === 0)
    ) {
      return _columns;
    } else if (!Object.keys(this.args.whitelist).includes(this.columnFilter)) {
      return _columns;
    } else {
      const filters = this.args.whitelist[this.columnFilter];
      return _columns.filter((item) => filters.includes(item.title));
    }
  }

  get totalColumnsLength() {
    return this.columns.filter((item) => item.visible).length;
  }

  @action
  labelFormatter(label, axis) {
    if (
      label.toLowerCase() ===
      this.intl.t('dictionary.unspecified').toLowerCase()
    )
      return label;
    if (this.args[axis] === 'source') {
      return _labelExtract(this.betaToggle.sourceOptions, label);
    } else if (this.args[axis] === 'status') {
      return _labelExtract(this.betaToggle.statusOptions, label);
    } else if (this.args[axis] === 'progress') {
      return _labelExtract(
        Object.entries(STATUS_KEYS).map(([k, v]) => ({
          label: this.intl.t(`_initiatives.updates_${v.toLowerCase()}`),
          value: k,
        })),
        label,
      );
    } else {
      return label;
    }
  }

  get statusMap() {
    return this.betaToggle.statusOptions.reduce((acc, item, index) => {
      // acc[item.value] = item.tableOrder;
      acc[item.value] = index;
      return acc;
    }, {});
  }

  get sourceMap() {
    return this.betaToggle.sourceOptions.reduce((acc, item) => {
      acc[item.value] = item.tableOrder;
      return acc;
    }, {});
  }

  @action
  sortStatusWithUnspecifiedToLast(a, b) {
    if (a === 'Unspecified') {
      return 1; // 'Unspecified' should be after anything else
    } else if (b === 'Unspecified') {
      return -1; // Anything else should be before 'Unspecified'
    } else {
      return this.statusMap[a] - this.statusMap[b]; // Sort by table order for other elements
    }
  }

  @action
  sortSourceWithUnspecifiedToLast(a, b) {
    if (a === 'Unspecified') {
      return 1; // 'Unspecified' should be after anything else
    } else if (b === 'Unspecified') {
      return -1; // Anything else should be before 'Unspecified'
    } else {
      return this.sourceMap[a] - this.sourceMap[b]; // Sort alphabetically for other elements
    }
  }

  groupObjectsByRowColumn(
    initiatives,
    rowFilter,
    hasMany = false,
    columns,
    rowLabelKey = 'self',
  ) {
    let _rows = {};

    //group items and items that belong in multiple columns get duplicated
    this.initiatives.forEach((initiative) => {
      const val = initiative?.[rowFilter];
      if (!val) {
        const title = this.intl.t('dictionary.unspecified');
        if (_rows[title]) _rows[title].data.push(initiative);
        else _rows[title] = { title: capitalize(title), data: [initiative] };
        return;
      }
      if (hasMany) {
        const items = val?.content;
        if (!items?.length) {
          const title = this.intl.t('dictionary.unspecified');
          if (_rows[title]) _rows[title].data.push(initiative);
          else _rows[title] = { title: capitalize(title), data: [initiative] };
        } else {
          items.forEach((item) => {
            const title = rowLabelKey === 'self' ? item : item.get(rowLabelKey);
            if (_rows[title]) _rows[title].data.push(initiative);
            else
              _rows[title] = {
                title: rowLabelKey === 'self' ? item : title,
                data: [initiative],
              };
          });
        }
        return;
      } else {
        const title =
          (!val && rowLabelKey === 'self') ||
          (val && !val?.get?.('content') && rowLabelKey !== 'self')
            ? this.intl.t('dictionary.unspecified')
            : rowLabelKey === 'self'
              ? val
              : val.get(rowLabelKey);
        if (_rows[title]) _rows[title].data.push(initiative);
        else _rows[title] = { title: capitalize(title), data: [initiative] };
      }
    });

    //grouped items are sorted so they always show in the same order
    const sortedRows = Object.keys(_rows)
      .sort(
        this.rowFilter === 'status'
          ? this.sortStatusWithUnspecifiedToLast
          : this.rowFilter === 'source'
            ? this.sortSourceWithUnspecifiedToLast
            : sortUnspecifiedColumnToLast,
      )
      .map((item) => {
        return _rows[item];
      });

    //construct nested column object to be iterated over
    sortedRows.forEach((item) => {
      let _columns = columns.reduce((acc, item) => {
        acc[item] = { title: item, data: [] };
        return acc;
      }, {});
      item.data.forEach((initiative) => {
        const columnObj = initiative[this.columnFilter];
        const objKey = this.columnLabelKeys[this.columnFilter];

        //if initiative attribute is simple value like string
        if (objKey === 'self') {
          const title = columnObj ?? this.intl.t('dictionary.unspecified');
          if (_columns[title]) _columns[title].data.push(initiative);
          return;
        }

        //if column value is null/empty
        if (!columnObj || (columnObj?.promise && !columnObj?.content)) {
          const title = this.intl.t('dictionary.unspecified');
          if (_columns[title]) _columns[title].data.push(initiative);
          return;
        }

        //if column value is a hasMany relationship
        if (this.filtersWithHasManyRelationships.includes(this.columnFilter)) {
          if (!columnObj?.content?.length) {
            const title = this.intl.t('dictionary.unspecified');
            if (_columns[title]) _columns[title].data.push(initiative);
          } else {
            columnObj?.content?.forEach((_item) => {
              if (_columns[_item.get(objKey)])
                _columns[_item.get(objKey)].data.push(initiative);
            });
          }
        }
        //if column value is a belongsTo relationship
        else {
          if (_columns[columnObj.get(objKey)])
            _columns[columnObj.get(objKey)].data.push(initiative);
        }
      });
      item.columns = Object.values(_columns);
    });

    return sortedRows;
  }

  get rows() {
    const hasMany = this.filtersWithHasManyRelationships.includes(
      this.rowFilter,
    );
    let rows = this.groupObjectsByRowColumn(
      this.initiatives,
      this.rowFilter,
      hasMany,
      this.columns.map((item) => item.title),
      this.rowLabelKeys[this.rowFilter],
    );

    if (
      !this.args.whitelist ||
      (typeof this.args.whitelist === 'object' &&
        Object.keys(this.args.whitelist).length === 0)
    );
    else if (!Object.keys(this.args.whitelist).includes(this.rowFilter));
    else {
      const filters = this.args.whitelist[this.rowFilter];
      rows = rows.filter((item) => filters.includes(item.title));
    }

    rows.forEach((item) => {
      item.visible = !this.hiddenRows[item.title];
      item.columns?.forEach((column) => {
        column.visible = !this.hiddenColumns[column.title];
      });
    });

    return rows;
  }

  @action
  dynamicGridWidth(columnsLength) {
    return Math.floor(this.totalColumnsLength / +columnsLength);
  }

  @action
  openSlideover(initiative_id) {
    this.initiativeSlideover.open(initiative_id);
  }

  get hiddenRows() {
    return this.args.hiddenRows;
  }

  get hiddenColumns() {
    return this.args.hiddenColumns;
  }

  @action
  handleRowCheckboxSelect(title, e) {
    const checked = e.target.checked;
    const hiddenRows = { ...this.hiddenRows };
    hiddenRows[title] = !checked;
    this.args.updateRowFilters(hiddenRows);
  }

  @action
  handleColumnCheckboxSelect(column, e) {
    const checked = e.target.checked;
    const hiddenColumns = { ...this.hiddenColumns };
    hiddenColumns[column] = !checked;
    this.args.updateColumnFilters(hiddenColumns);
  }
}
